/**
 * The one-time calendar card over the grid: whether it stands and what it offers, the trip to
 * Google, „Dodaj do Kalendarza Apple", and „Nie teraz" (`model/googleCalendarPrompt`). Both reads
 * are their entities' one cache entries; `useBookingCalendar` waits for them in the screen's gate,
 * so the card is decided on the grid's own hand-over.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { postMeCalendarFeed } from '~/shared/api/generated/endpoints/my-calendar-feed/my-calendar-feed';
import { postMeCalendarConnectionsGooglePromptAnswer } from '~/shared/api/generated/endpoints/my-google-calendar-connection/my-google-calendar-connection';
import type {
  TrainWithMeModulesTrainingDtosResponsesCalendarFeedResponse as CalendarFeed,
  TrainWithMeModulesTrainingDtosResponsesGoogleCalendarConnectionResponse as GoogleConnectionDto,
} from '~/shared/api/generated/model';
import { ApiError } from '~/shared/api/mutator';
import { isApplePlatform } from '~/shared/lib/applePlatform';
import type { SkeletonGate } from '~/shared/lib/useSkeletonGate';
import {
  calendarFeedQueryKey,
  calendarFeedQueryOptions,
  isWebcalUrl,
  openInCalendar,
  subscribeTarget,
} from '~/entities/calendar-feed';
import {
  goToGoogleConsent,
  googleCalendarQueryKey,
  googleCalendarQueryOptions,
} from '~/entities/google-calendar-connection';

import {
  calendarPromptOffer,
  calendarPromptOptions,
  calendarPromptShown,
  type CalendarPromptOptions,
  type GoogleCalendarPromptOffer,
} from '../model/googleCalendarPrompt';

/** Why „Dodaj do Kalendarza Apple" could not start: the account tab's own two sentences. */
export type AppleSubscribeProblem = 'failed' | 'unavailableNow';

export interface GoogleCalendarPromptApi {
  readonly shown: boolean;
  /** What the card offers while it stands — Google, Apple Calendar or both. */
  readonly options: CalendarPromptOptions;
  /** The hand-over class the card arrived with, kept: a later wait of the grid must not replay it. */
  readonly reveal: string;
  /** `authorize-url` is out; the page leaves for Google when it answers. */
  readonly connecting: boolean;
  /** The trip could not start. */
  readonly connectFailed: boolean;
  /** The feed is being switched on; the browser goes to Calendar when it answers. */
  readonly subscribing: boolean;
  readonly appleProblem: AppleSubscribeProblem | null;
  readonly onConnect: () => void;
  /** As Integracje's button does — enable the feed if needed, open `webcal:` — and answers the prompt. */
  readonly onApple: () => void;
  readonly onNotNow: () => void;
}

const NONE: CalendarPromptOptions = { google: false, apple: false };

export function useGoogleCalendarPrompt(gate: SkeletonGate): GoogleCalendarPromptApi {
  const queryClient = useQueryClient();
  const read = useQuery(googleCalendarQueryOptions());
  const appleDevice = isApplePlatform(globalThis.navigator);
  const feed = useQuery(calendarFeedQueryOptions(appleDevice));
  const options = calendarPromptOptions(read.data, feed.data, appleDevice);

  // Adjusted during render, so the card commits on the same frame as the grid it stands over.
  const [decided, setDecided] = useState<{ offer: GoogleCalendarPromptOffer; reveal: string }>({
    offer: 'undecided',
    reveal: '',
  });
  const offer = calendarPromptOffer(decided.offer, !gate.waiting, options);
  if (offer !== decided.offer) setDecided({ offer, reveal: gate.reveal });

  const [answeredHere, setAnsweredHere] = useState(false);
  const answer = useMutation({
    mutationFn: () => postMeCalendarConnectionsGooglePromptAnswer(),
    onSuccess: (next) => {
      queryClient.setQueryData(googleCalendarQueryKey, next);
    },
    // A refusal keeps the card away for this visit; the next read decides after that.
  });
  const answerHere = () => {
    setAnsweredHere(true);
    queryClient.setQueryData<GoogleConnectionDto>(googleCalendarQueryKey, (current) =>
      current === undefined ? current : { ...current, promptAnswered: true },
    );
    answer.mutate();
  };

  const [appleProblem, setAppleProblem] = useState<AppleSubscribeProblem | null>(null);
  /** Integracje's own enable (`useAppleCalendar`): the feed's answer primes the shared read. */
  const enable = useMutation({
    mutationFn: () => postMeCalendarFeed(),
    onSuccess: (next: CalendarFeed) => {
      queryClient.setQueryData(calendarFeedQueryKey, next);
      // The feed is on: the trainer chose Apple, so the card is answered for good.
      answerHere();
      if (isWebcalUrl(next.webcalUrl)) openInCalendar(next.webcalUrl);
    },
    onError: (error) => {
      const unavailable = error instanceof ApiError && error.code === 'calendar_feed_unavailable';
      setAppleProblem(unavailable ? 'unavailableNow' : 'failed');
      void queryClient.invalidateQueries({ queryKey: calendarFeedQueryKey });
    },
  });

  const [connecting, setConnecting] = useState(false);
  const [connectFailed, setConnectFailed] = useState(false);
  const trip = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      trip.current?.abort();
    },
    [],
  );

  /* Back from Google's page restores this document from the back-forward cache with the press
   * still pending: the button is freed and the read asked again. */
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      setConnecting(false);
      void queryClient.invalidateQueries({ queryKey: googleCalendarQueryKey });
    };
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [queryClient]);

  const shown = calendarPromptShown(decided.offer, options, answeredHere);

  return {
    shown: shown !== null,
    options: shown ?? NONE,
    reveal: decided.reveal,
    connecting,
    connectFailed,
    subscribing: enable.isPending,
    appleProblem,
    onConnect: () => {
      if (connecting) return;
      setConnecting(true);
      setConnectFailed(false);
      const controller = new AbortController();
      trip.current = controller;
      goToGoogleConsent(controller.signal).catch(() => {
        if (controller.signal.aborted) return;
        setConnecting(false);
        setConnectFailed(true);
        // A 409 says Google is not offered here any more; the read takes the card away.
        void queryClient.invalidateQueries({ queryKey: googleCalendarQueryKey });
      });
    },
    onApple: () => {
      if (enable.isPending) return;
      setAppleProblem(null);
      const target = subscribeTarget(feed.data);
      if (target === null) {
        enable.mutate();
        return;
      }
      answerHere();
      openInCalendar(target);
    },
    onNotNow: answerHere,
  };
}
